import { useQuery } from '@tanstack/react-query';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { YoutubePreview } from '@/components/content/youtube-preview';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { isApiError } from '@/lib/api-errors';
import { formatDate, formatDateTime } from '@/utils/format';

import type { ContentItemDbType } from './content-comment';

type ContentStatus = 'draft' | 'scheduled' | 'published';

interface SermonDetail {
  id: string;
  title: string;
  preacher: string | null;
  preachedOn: string | null;
  programName: string | null;
  occurrenceDate: string | null;
  description: string | null;
  scriptureRefs: string[];
  audioUrl: string | null;
  videoUrl: string | null;
  artworkUrl: string | null;
  status: ContentStatus;
  publishAt: string | null;
  publishedAt: string | null;
  createdAt: string;
}

interface DevotionalDetail {
  id: string;
  title: string;
  devotionalDate: string;
  scriptureRef: string | null;
  body: string;
  prayer: string | null;
  reflectionQuestion: string | null;
  imageUrl: string | null;
  audioUrl: string | null;
  notifyOnPublish: boolean;
  status: ContentStatus;
  scheduledFor: string | null;
  publishedAt: string | null;
}

interface AnnouncementDetail {
  id: string;
  title: string;
  body: string;
  imageUrl: string | null;
  publishAt: string | null;
  expiresAt: string | null;
  pinned: boolean;
  status: ContentStatus;
  publishedAt: string | null;
}

interface VideoDetail {
  id: string;
  title: string;
  description: string | null;
  youtubeUrl: string;
  status: ContentStatus;
  kind: 'video' | 'live';
  publishAt: string | null;
  publishedAt: string | null;
  createdAt: string;
}

interface GalleryDetail {
  id: string;
  title: string;
  categoryName: string | null;
  status: ContentStatus;
  publishAt: string | null;
  publishedAt: string | null;
  publishedOn: string;
  photoCount: number;
  coverThumbnailUrl: string | null;
}

type LoadedItem =
  | { type: 'sermon'; item: SermonDetail }
  | { type: 'devotional'; item: DevotionalDetail }
  | { type: 'announcement'; item: AnnouncementDetail }
  | { type: 'video'; item: VideoDetail }
  | { type: 'gallery'; item: GalleryDetail };

const ITEM_PATH: Record<ContentItemDbType, (id: string) => string> = {
  sermon: (id) => `/content/sermons/${id}`,
  devotional: (id) => `/content/devotionals/${id}`,
  announcement: (id) => `/content/announcements/${id}`,
  video: (id) => `/content/videos/${id}`,
  gallery: (id) => `/content/galleries/${id}`,
};

const contentStatusVariant = (status: ContentStatus): BadgeVariant => {
  if (status === 'published') return 'success';
  if (status === 'scheduled') return 'info';
  return 'neutral';
};

const serviceLabel = (
  programName: string | null | undefined,
  occurrenceDate: string | null | undefined,
): string => {
  if (!programName && !occurrenceDate) return '—';
  if (programName && occurrenceDate) {
    return `${programName} · ${formatDate(occurrenceDate)}`;
  }
  return programName ?? (occurrenceDate ? formatDate(occurrenceDate) : '—');
};

const loadItem = async (contentType: ContentItemDbType, contentId: string): Promise<LoadedItem> => {
  switch (contentType) {
    case 'sermon':
      return { type: 'sermon', item: await api.get<SermonDetail>(ITEM_PATH.sermon(contentId)) };
    case 'devotional':
      return {
        type: 'devotional',
        item: await api.get<DevotionalDetail>(ITEM_PATH.devotional(contentId)),
      };
    case 'announcement':
      return {
        type: 'announcement',
        item: await api.get<AnnouncementDetail>(ITEM_PATH.announcement(contentId)),
      };
    case 'video':
      return { type: 'video', item: await api.get<VideoDetail>(ITEM_PATH.video(contentId)) };
    case 'gallery':
      return { type: 'gallery', item: await api.get<GalleryDetail>(ITEM_PATH.gallery(contentId)) };
  }
};

const Field = ({ label, value }: { label: string; value: ReactNode }) => (
  <div>
    <dt className="text-caption text-fg-secondary">{label}</dt>
    <dd className="text-body-sm">{value}</dd>
  </div>
);

const SermonBody = ({ item }: { item: SermonDetail }) => {
  const { t } = useTranslation('content');
  const publishAt = item.publishAt ?? item.publishedAt;

  return (
    <div className="flex flex-col gap-6">
      {item.videoUrl ? (
        <YoutubePreview
          url={item.videoUrl}
          title={t('sermons.previewTitle', { title: item.title })}
          unavailableLabel={t('sermons.previewUnavailable')}
          watchLabel={t('sermons.watchOnYoutube')}
        />
      ) : null}
      {item.audioUrl ? (
        <div>
          <p className="text-caption text-fg-secondary">{t('sermons.audioLabel')}</p>
          <audio controls src={item.audioUrl} className="mt-2 w-full" preload="metadata">
            <track kind="captions" />
          </audio>
        </div>
      ) : null}
      <div className="flex items-start gap-4">
        {item.artworkUrl ? (
          <img
            src={item.artworkUrl}
            alt={t('sermons.artworkAlt', { title: item.title })}
            className="size-20 shrink-0 rounded-lg object-cover"
          />
        ) : null}
        <div className="min-w-0 flex-1 space-y-4">
          <Badge variant={contentStatusVariant(item.status)}>{t(`status.${item.status}`)}</Badge>
          <dl className="grid gap-3 sm:grid-cols-2">
            <Field label={t('sermons.columns.createdAt')} value={formatDateTime(item.createdAt)} />
            <Field
              label={t('sermons.columns.publishAt')}
              value={publishAt ? formatDateTime(publishAt) : '—'}
            />
            <Field
              label={t('sermons.columns.service')}
              value={serviceLabel(item.programName, item.occurrenceDate)}
            />
            <Field label={t('sermons.columns.preacher')} value={item.preacher ?? '—'} />
            <Field
              label={t('sermons.preachedOnLabel')}
              value={item.preachedOn ? formatDate(item.preachedOn) : '—'}
            />
            <Field
              label={t('sermons.versesLabel')}
              value={item.scriptureRefs.length > 0 ? item.scriptureRefs.join(' · ') : '—'}
            />
          </dl>
          {item.description ? (
            <div>
              <p className="text-caption text-fg-secondary">{t('sermons.summaryLabel')}</p>
              <p className="mt-1 whitespace-pre-wrap text-body-sm">{item.description}</p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const DevotionalBody = ({ item }: { item: DevotionalDetail }) => {
  const { t } = useTranslation('content');
  const { t: tCommon } = useTranslation();
  const publishAt = item.scheduledFor ?? item.publishedAt;

  return (
    <div className="flex items-start gap-4">
      {item.imageUrl ? (
        <img
          src={item.imageUrl}
          alt={t('devotionals.artworkAlt', { title: item.title })}
          className="size-20 shrink-0 rounded-lg object-cover"
        />
      ) : null}
      <div className="min-w-0 flex-1 space-y-4">
        <Badge variant={contentStatusVariant(item.status)}>{t(`status.${item.status}`)}</Badge>
        <dl className="grid gap-3 sm:grid-cols-2">
          <Field label={t('devotionals.columns.date')} value={formatDate(item.devotionalDate)} />
          <Field
            label={t('devotionals.columns.publishAt')}
            value={publishAt ? formatDateTime(publishAt) : '—'}
          />
          <Field label={t('devotionals.scriptureLabel')} value={item.scriptureRef ?? '—'} />
          <Field
            label={t('devotionals.notifyLabel')}
            value={item.notifyOnPublish ? tCommon('boolean.yes') : tCommon('boolean.no')}
          />
        </dl>
        <div>
          <p className="text-caption text-fg-secondary">{t('devotionals.bodyLabel')}</p>
          <p className="mt-1 whitespace-pre-wrap text-body-sm">{item.body}</p>
        </div>
        {item.reflectionQuestion ? (
          <div>
            <p className="text-caption text-fg-secondary">{t('devotionals.reflectionLabel')}</p>
            <p className="mt-1 whitespace-pre-wrap text-body-sm">{item.reflectionQuestion}</p>
          </div>
        ) : null}
        {item.prayer ? (
          <div>
            <p className="text-caption text-fg-secondary">{t('devotionals.prayerLabel')}</p>
            <p className="mt-1 whitespace-pre-wrap text-body-sm">{item.prayer}</p>
          </div>
        ) : null}
        {item.audioUrl ? (
          <div>
            <p className="text-caption text-fg-secondary">{t('devotionals.audioLabel')}</p>
            <audio controls src={item.audioUrl} className="mt-2 w-full" preload="metadata">
              <track kind="captions" />
            </audio>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const AnnouncementBody = ({ item }: { item: AnnouncementDetail }) => {
  const { t } = useTranslation('content');
  const { t: tCommon } = useTranslation();
  const publishAt = item.publishAt ?? item.publishedAt;

  return (
    <div className="flex flex-col gap-6">
      <div className="overflow-hidden rounded-lg border border-border bg-surface">
        {item.imageUrl ? (
          <img
            src={item.imageUrl}
            alt={t('announcements.imageAlt', { title: item.title })}
            className="max-h-64 w-full object-cover"
          />
        ) : null}
        <div className="space-y-3 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-card-title font-semibold text-fg">{item.title}</p>
            {item.pinned ? <Badge variant="info">{t('announcements.pinnedYes')}</Badge> : null}
          </div>
          <p className="whitespace-pre-wrap text-body-sm text-fg">{item.body}</p>
        </div>
      </div>
      <div className="space-y-4">
        <Badge variant={contentStatusVariant(item.status)}>{t(`status.${item.status}`)}</Badge>
        <dl className="grid gap-3 sm:grid-cols-2">
          <Field
            label={t('announcements.columns.publishAt')}
            value={publishAt ? formatDateTime(publishAt) : '—'}
          />
          <Field
            label={t('announcements.columns.expiresAt')}
            value={item.expiresAt ? formatDateTime(item.expiresAt) : '—'}
          />
          <Field
            label={t('announcements.pinnedLabel')}
            value={item.pinned ? tCommon('boolean.yes') : tCommon('boolean.no')}
          />
        </dl>
      </div>
    </div>
  );
};

const VideoBody = ({ item }: { item: VideoDetail }) => {
  const { t } = useTranslation('content');
  const publishAt = item.publishAt ?? item.publishedAt;

  return (
    <div className="flex flex-col gap-6">
      <YoutubePreview
        url={item.youtubeUrl}
        title={t('videos.previewTitle', { title: item.title })}
        unavailableLabel={t('videos.previewUnavailable')}
        watchLabel={t('videos.watchOnYoutube')}
      />
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={contentStatusVariant(item.status)}>{t(`status.${item.status}`)}</Badge>
          <span className="text-body-sm text-fg-secondary">
            {item.kind === 'live' ? t('videos.kindLive') : t('videos.kindVideo')}
          </span>
        </div>
        <dl className="grid gap-3 sm:grid-cols-2">
          <Field label={t('videos.columns.createdAt')} value={formatDateTime(item.createdAt)} />
          <Field
            label={t('videos.columns.publishAt')}
            value={publishAt ? formatDateTime(publishAt) : '—'}
          />
        </dl>
        {item.description ? (
          <div>
            <p className="text-caption text-fg-secondary">{t('videos.descriptionLabel')}</p>
            <p className="mt-1 whitespace-pre-wrap text-body-sm">{item.description}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const GalleryBody = ({ item }: { item: GalleryDetail }) => {
  const { t } = useTranslation('content');
  const publishAt = item.publishAt ?? item.publishedAt;

  return (
    <div className="flex items-start gap-4">
      {item.coverThumbnailUrl ? (
        <img
          src={item.coverThumbnailUrl}
          alt={item.title}
          className="size-20 shrink-0 rounded-lg object-cover"
        />
      ) : null}
      <div className="min-w-0 flex-1 space-y-4">
        <Badge variant={contentStatusVariant(item.status)}>
          {item.status === 'published' ? t('galleries.status.published') : t(`status.${item.status}`)}
        </Badge>
        <dl className="grid gap-3 sm:grid-cols-2">
          <Field label={t('galleries.columns.publishedOn')} value={formatDate(item.publishedOn)} />
          <Field
            label={t('galleries.columns.publishAt')}
            value={publishAt ? formatDateTime(publishAt) : '—'}
          />
          <Field label={t('galleries.columns.category')} value={item.categoryName ?? '—'} />
          <Field
            label={t('galleries.columns.photos')}
            value={t('galleries.photoCount', { count: item.photoCount })}
          />
        </dl>
      </div>
    </div>
  );
};

const ItemBody = ({ loaded }: { loaded: LoadedItem }) => {
  switch (loaded.type) {
    case 'sermon':
      return <SermonBody item={loaded.item} />;
    case 'devotional':
      return <DevotionalBody item={loaded.item} />;
    case 'announcement':
      return <AnnouncementBody item={loaded.item} />;
    case 'video':
      return <VideoBody item={loaded.item} />;
    case 'gallery':
      return <GalleryBody item={loaded.item} />;
  }
};

export const ContentItemDetailDialog = ({
  contentType,
  contentId,
  open,
  onOpenChange,
}: {
  contentType: ContentItemDbType | null;
  contentId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation('content');
  const { t: tCommon } = useTranslation();
  const navigate = useNavigate();

  const detail = useQuery({
    queryKey: ['content', 'item', contentType, contentId],
    queryFn: () => loadItem(contentType!, contentId!),
    enabled: open && Boolean(contentType && contentId),
  });

  const title =
    detail.data?.item.title ??
    (contentType ? t(`comments.contentType.${contentType}`) : t('comments.detail.postTitle'));

  const unavailable = detail.isError && isApiError(detail.error) && detail.error.status === 404;

  let body: ReactNode = null;
  if (detail.isLoading) {
    body = (
      <div className="space-y-3">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    );
  } else if (detail.isError) {
    body = (
      <p className="text-body-sm text-fg-secondary">
        {unavailable ? t('comments.detail.unavailable') : tCommon('state.errorBody')}
      </p>
    );
  } else if (detail.data) {
    body = <ItemBody loaded={detail.data} />;
  }

  const galleryId = detail.data?.type === 'gallery' ? detail.data.item.id : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" closeLabel={tCommon('action.close')} className="overflow-visible">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody contentClassName="flex flex-col gap-6">{body}</DialogBody>
        {galleryId ? (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {tCommon('action.close')}
            </Button>
            <Button
              onClick={() => {
                onOpenChange(false);
                void navigate(`/content/galleries/${galleryId}`);
              }}
            >
              {t('comments.detail.openGallery')}
            </Button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
